// src/auth/auth0-bridge.js
// Guarantees a FRESH ID token. If token is near-expiry or a 401 just happened,
// we force a silent refresh (getAccessTokenSilently({ cacheMode: "off" })) and
// then read a new ID token from getIdTokenClaims().

let _getAccessTokenSilently = null;
let _getIdTokenClaims = null;
let _loginWithRedirect = null;
let _isAuthenticatedFn = null;
let _isLoadingFn = null;

export function bindAuth0({
  getAccessTokenSilently,
  getIdTokenClaims,
  loginWithRedirect,
  isAuthenticatedFn,
  isLoadingFn,
}) {
  _getAccessTokenSilently = getAccessTokenSilently;
  _getIdTokenClaims = getIdTokenClaims;
  _loginWithRedirect = loginWithRedirect;
  _isAuthenticatedFn = isAuthenticatedFn;
  _isLoadingFn = isLoadingFn;
}

export function isAuthReady() {
  if (!_isAuthenticatedFn || !_isLoadingFn) return false;
  return !_isLoadingFn() && !!_isAuthenticatedFn();
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

async function waitUntilAuthSettled(timeoutMs = 8000) {
  const start = Date.now();
  while (_isLoadingFn && _isLoadingFn()) {
    if (Date.now() - start > timeoutMs) break;
    await sleep(50);
  }
}

function decodeJwt(token) {
  try {
    const [, payload] = token.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

function willExpireSoon(token, bufferSec = 120) {
  const { exp } = decodeJwt(token);
  if (!exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return exp <= now + bufferSec;
}

/**
 * Get a FRESH ID token.
 * - If token is near expiry OR forceRefresh=true, we force a silent refresh first.
 * - After refresh, we always read the new ID token from getIdTokenClaims().
 */
export async function fetchFreshIdTokenOrThrow({
  interactiveLogin = false,
  forceRefresh = false, // set true after a 401
} = {}) {
  if (!_getAccessTokenSilently || !_getIdTokenClaims) {
    throw new Error("[Auth0] Bridge not initialized. Mount <Auth0Binder />.");
  }

  await waitUntilAuthSettled(8000);

  const authed = _isAuthenticatedFn ? _isAuthenticatedFn() : false;
  if (!authed) {
    if (interactiveLogin && _loginWithRedirect) {
      await _loginWithRedirect();
      throw new Error("[Auth0] Triggered login.");
    }
    throw new Error("[Auth0] Not authenticated.");
  }

  // Helper to read current ID token from cache/SDK
  const readIdToken = async () => {
    const claims = await _getIdTokenClaims();
    return claims && (claims.__raw || claims.raw)
      ? claims.__raw || claims.raw
      : null;
  };

  let idToken = await readIdToken();

  // If we don't have one, or it's near expiry, or caller asked to force refresh -> refresh
  if (!idToken || willExpireSoon(idToken) || forceRefresh) {
    try {
      // Force refresh of the session/tokens (refresh_token flow)
      await _getAccessTokenSilently({
        detailedResponse: true,
        cacheMode: "off",
      });
      idToken = await readIdToken();
    } catch (e) {
      // If forced refresh failed but we *do* have an ID token, return it as last resort
      if (idToken) return idToken;
      throw e;
    }
  }

  // If after all that it's still missing, throw
  if (!idToken) throw new Error("[Auth0] Failed to obtain ID token.");
  return idToken;
}
